import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { CreateSourcePayload, Platform } from '../../api/sources'
import { topicsApi, Topic } from '../../api/topics'

const PLATFORMS: { value: Platform; label: string }[] = [
  { value: 'web',      label: 'Web (URL)' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'reddit',   label: 'Reddit' },
  { value: 'bluesky',  label: 'Bluesky' },
  { value: 'twitter',  label: 'X / Twitter' },
  { value: 'rss',      label: 'RSS Feed' },
  { value: 'darkweb',  label: 'Dark Web (.onion)' },
]

interface AddSourceModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (payload: CreateSourcePayload) => Promise<void>
}

export function AddSourceModal({ open, onClose, onSubmit }: AddSourceModalProps) {
  const [name, setName]             = useState('')
  const [handle, setHandle]         = useState('')
  const [platform, setPlatform]     = useState<Platform>('web')
  const [credibility, setCredibility] = useState(50)
  const [selectedTopicIds, setSelectedTopicIds] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState('')

  const { data: topics = [] } = useQuery<Topic[]>({
    queryKey: ['topics'],
    queryFn: topicsApi.list,
    staleTime: 60_000,
  })

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim() || !handle.trim()) { setError('Name and URL/handle are required.'); return }
    setError('')
    setSubmitting(true)
    try {
      await onSubmit({
        name: name.trim(),
        url_or_handle: handle.trim(),
        platform,
        credibility_score: credibility,
        ...(selectedTopicIds.length > 0 ? { topic_ids: selectedTopicIds } : {}),
      })
      setName(''); setHandle(''); setPlatform('web'); setCredibility(50); setSelectedTopicIds([])
      onClose()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail ?? 'Failed to add source.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add source"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button form="add-source-form" type="submit" loading={submitting}>Add source</Button>
        </>
      }
    >
      <form id="add-source-form" onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <label htmlFor="src-name" className="block text-xs font-medium text-text-secondary mb-1.5">
            Source name <span className="text-signal-high">*</span>
          </label>
          <input
            id="src-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-anveshak-bg border border-anveshak-border rounded px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-anveshak-accent"
            placeholder="e.g. South China Morning Post"
          />
        </div>

        <div>
          <label htmlFor="src-platform" className="block text-xs font-medium text-text-secondary mb-1.5">Platform</label>
          <select
            id="src-platform"
            value={platform}
            onChange={(e) => setPlatform(e.target.value as Platform)}
            className="w-full bg-anveshak-bg border border-anveshak-border rounded px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-anveshak-accent"
          >
            {PLATFORMS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="src-handle" className="block text-xs font-medium text-text-secondary mb-1.5">
            URL / Handle <span className="text-signal-high">*</span>
          </label>
          <input
            id="src-handle"
            type="text"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            className="w-full bg-anveshak-bg border border-anveshak-border rounded px-3 py-2 text-sm text-text-primary font-mono focus:outline-none focus:border-anveshak-accent"
            placeholder={platform === 'web' ? 'https://example.com' : platform === 'darkweb' ? 'http://example.onion' : platform === 'reddit' ? 'r/worldnews' : '@handle'}
          />
        </div>

        <div>
          <label htmlFor="src-cred" className="block text-xs font-medium text-text-secondary mb-1.5">
            Initial credibility score: <span className="text-anveshak-accent font-semibold">{credibility}</span>
          </label>
          <input
            id="src-cred"
            type="range"
            min={0}
            max={100}
            value={credibility}
            onChange={(e) => setCredibility(Number(e.target.value))}
            className="w-full accent-anveshak-accent"
            aria-label={`Credibility score: ${credibility}`}
          />
          <div className="flex justify-between text-[10px] text-text-muted mt-1">
            <span>0 (untrusted)</span><span>50</span><span>100 (trusted)</span>
          </div>
        </div>

        {topics.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Link to topics
            </label>
            <div className="space-y-1 max-h-32 overflow-y-auto border border-anveshak-border rounded p-2">
              {topics.filter((t) => t.status === 'active').map((t) => (
                <label key={t.id} className="flex items-center gap-2 cursor-pointer text-sm text-text-primary hover:bg-anveshak-muted/50 rounded px-1 py-0.5">
                  <input
                    type="checkbox"
                    checked={selectedTopicIds.includes(t.id)}
                    onChange={(e) => {
                      setSelectedTopicIds((prev) =>
                        e.target.checked ? [...prev, t.id] : prev.filter((id) => id !== t.id)
                      )
                    }}
                    className="accent-anveshak-accent"
                  />
                  {t.name}
                </label>
              ))}
            </div>
            {selectedTopicIds.length === 0 && (
              <p className="text-[10px] text-yellow-400 mt-1">
                Sources not linked to any topic will not be scraped.
              </p>
            )}
          </div>
        )}

        {error && (
          <p role="alert" className="text-signal-high text-xs bg-signal-high/10 border border-signal-high/20 rounded px-3 py-2">
            {error}
          </p>
        )}
      </form>
    </Modal>
  )
}
